import type { ChartingNoteFormValues } from '@/utils/charting-note/buildChartingNoteSummary';
import { registerSessionCacheInvalidator } from '@/services/auth-session';

export type ProgressNoteSessionState = {
    formValues: ChartingNoteFormValues;
    savedSnapshot: ChartingNoteFormValues;
    savedAt: string | null;
    entryCount: number;
    progressNoteId: string | null;
    isEditing?: boolean;
};

const sessionCache = new Map<string, ProgressNoteSessionState>();
let sessionCacheRevision = 0;
const sessionCacheListeners = new Set<() => void>();

const notifySessionCacheListeners = (): void => {
    sessionCacheRevision += 1;
    sessionCacheListeners.forEach((listener) => listener());
};

export const subscribeProgressNoteSessionCache = (listener: () => void): (() => void) => {
    sessionCacheListeners.add(listener);
    return () => sessionCacheListeners.delete(listener);
};

export const getProgressNoteSessionCacheRevision = (): number => sessionCacheRevision;

export const hasUnsavedProgressNoteChanges = (session: ProgressNoteSessionState): boolean =>
    JSON.stringify(session.formValues) !== JSON.stringify(session.savedSnapshot);

export const hasUnsavedProgressNoteSession = (patientId: string): boolean => {
    const session = sessionCache.get(patientId);
    return session ? hasUnsavedProgressNoteChanges(session) : false;
};

export const getProgressNoteSession = (patientId: string): ProgressNoteSessionState | undefined => sessionCache.get(patientId);

export const setProgressNoteSession = (patientId: string, state: ProgressNoteSessionState): void => {
    sessionCache.set(patientId, state);
    notifySessionCacheListeners();
};

export const clearProgressNoteSession = (patientId: string): void => {
    if (!sessionCache.delete(patientId)) {
        return;
    }
    notifySessionCacheListeners();
};

export const clearProgressNoteSessionCache = (): void => {
    sessionCache.clear();
    notifySessionCacheListeners();
};

registerSessionCacheInvalidator(clearProgressNoteSessionCache);
